/**
 * User Service
 * Manages user profiles in Firestore
 */

import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { UserProfile } from '@/types/baby';
import { firestoreToUser } from '@/types/baby';

/**
 * Create or update user profile
 */
export async function createOrUpdateUserProfile(
  uid: string,
  email: string,
  displayName: string | null,
  photoURL: string | null
): Promise<void> {
  try {
    const userRef = doc(db, 'users', uid);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      // Update existing user
      await updateDoc(userRef, {
        lastActiveAt: serverTimestamp(),
        displayName,
        photoURL,
      });
    } else {
      // Create new user
      await setDoc(userRef, {
        email,
        displayName,
        photoURL,
        createdAt: serverTimestamp(),
        lastActiveAt: serverTimestamp(),
      });
    }
  } catch (error) {
    console.error('Error creating/updating user profile:', error);
    throw error;
  }
}

/**
 * Get user profile
 */
export async function getUserProfile(uid: string): Promise<UserProfile | null> {
  try {
    const userRef = doc(db, 'users', uid);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      return firestoreToUser(uid, userSnap.data());
    }
    return null;
  } catch (error) {
    console.error('Error getting user profile:', error);
    return null;
  }
}

/**
 * Update last active timestamp
 */
export async function updateLastActive(uid: string): Promise<void> {
  try {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, {
      lastActiveAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating last active:', error);
  }
}
